import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Calendar, Plus, X, Users, ChevronLeft, ChevronRight, Trash2, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';

const SPECIALTIES = { electrical: 'Eléctrica', structural: 'Estructura', civil: 'Civil', commissioning: 'Comisionamiento', general: 'General' };
const PRIORITIES = { low: { label: 'Baja', color: '#64748B' }, normal: { label: 'Normal', color: '#3B82F6' }, high: { label: 'Alta', color: '#F59E0B' }, urgent: { label: 'Urgente', color: '#EF4444' } };
const COLORS = ['#D4A017', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', '#EF4444', '#06B6D4', '#F59E0B'];

export default function CrewCalendar() {
  const [crews, setCrews] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(getMonday(new Date()));
  const [showCreateCrew, setShowCreateCrew] = useState(false);
  const [showCreateAssign, setShowCreateAssign] = useState(null);
  const [showMembers, setShowMembers] = useState(null);
  const [members, setMembers] = useState([]);
  const [crewForm, setCrewForm] = useState({ name: '', specialty: 'general', color: '#D4A017' });
  const [assignForm, setAssignForm] = useState({ crewId: '', projectId: '', title: '', assignmentDate: '', endDate: '', startTime: '08:00', endTime: '17:00', priority: 'normal', description: '' });
  const [memberForm, setMemberForm] = useState({ externalName: '', externalPhone: '', roleInCrew: 'member' });

  useEffect(() => { loadAll(); }, [currentWeek]);

  function getMonday(d) { const day = d.getDay(); const diff = d.getDate() - day + (day === 0 ? -6 : 1); return new Date(d.setDate(diff)); }
  function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
  function formatDate(d) { return d.toISOString().split('T')[0]; }
  function getDayLabel(d) { return d.toLocaleDateString('es', { weekday: 'short', day: 'numeric', month: 'short' }); }

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));

  const loadAll = async () => {
    try {
      const from = formatDate(currentWeek);
      const to = formatDate(addDays(currentWeek, 6));
      const [cr, as, pr] = await Promise.all([
        api.get('/crews'), api.get('/crews/assignments', { params: { from, to } }), api.get('/projects'),
      ]);
      setCrews(cr.data); setAssignments(as.data); setProjects(pr.data.data || pr.data || []);
    } catch (e) {}
  };

  const loadMembers = async (crewId) => {
    try { const r = await api.get(`/crews/${crewId}/members`); setMembers(r.data); } catch (e) {}
  };

  const createCrew = async () => {
    if (!crewForm.name) return toast.error('Nombre requerido');
    try {
      await api.post('/crews', crewForm); toast.success('Cuadrilla creada');
      setShowCreateCrew(false); loadAll(); setCrewForm({ name: '', specialty: 'general', color: '#D4A017' });
    } catch (e) { toast.error('Error'); }
  };

  const createAssignment = async () => {
    if (!assignForm.crewId || !assignForm.projectId || !assignForm.title || !assignForm.assignmentDate) return toast.error('Campos requeridos');
    try {
      await api.post('/crews/assignments', assignForm); toast.success('Asignación creada');
      setShowCreateAssign(null); loadAll();
      setAssignForm({ crewId: '', projectId: '', title: '', assignmentDate: '', endDate: '', startTime: '08:00', endTime: '17:00', priority: 'normal', description: '' });
    } catch (e) { toast.error('Error'); }
  };

  const addMember = async () => {
    if (!memberForm.externalName) return toast.error('Nombre requerido');
    try {
      await api.post(`/crews/${showMembers.id}/members`, memberForm);
      toast.success('Miembro añadido'); loadMembers(showMembers.id);
      setMemberForm({ externalName: '', externalPhone: '', roleInCrew: 'member' });
    } catch (e) { toast.error('Error'); }
  };

  const removeMember = async (id) => {
    try { await api.delete(`/crews/members/${id}`); loadMembers(showMembers.id); } catch (e) { toast.error('Error'); }
  };

  const deleteAssignment = async (id) => {
    if (!window.confirm('¿Eliminar asignación?')) return;
    try { await api.delete(`/crews/assignments/${id}`); loadAll(); } catch (e) { toast.error('Error'); }
  };

  const deleteCrew = async (id) => {
    if (!window.confirm('¿Desactivar cuadrilla?')) return;
    try { await api.delete(`/crews/${id}`); loadAll(); } catch (e) { toast.error('Error'); }
  };

  const getAssignmentsForDay = (crewId, date) => {
    const d = formatDate(date);
    return assignments.filter(a => a.crew_id === crewId && a.assignment_date <= d && (a.end_date >= d || a.assignment_date === d));
  };

  return (
    <div className="crew-page">
      <div className="page-header">
        <div><h1><Calendar size={24} /> Cuadrillas</h1><p className="subtitle">Planificación y calendario de equipos</p></div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setShowCreateCrew(true)}><Users size={16} /> Nueva Cuadrilla</button>
          <button className="btn btn-primary" onClick={() => setShowCreateAssign({})}><Plus size={16} /> Asignar</button>
        </div>
      </div>

      {/* Week navigation */}
      <div className="crew-week-nav">
        <button className="btn-icon" onClick={() => setCurrentWeek(addDays(currentWeek, -7))}><ChevronLeft size={18} /></button>
        <span className="crew-week-label">{getDayLabel(weekDays[0])} — {getDayLabel(weekDays[6])}</span>
        <button className="btn-icon" onClick={() => setCurrentWeek(addDays(currentWeek, 7))}><ChevronRight size={18} /></button>
        <button className="btn btn-sm btn-secondary" onClick={() => setCurrentWeek(getMonday(new Date()))}>Hoy</button>
      </div>

      {/* Calendar Grid */}
      <div className="crew-calendar">
        <div className="crew-cal-header">
          <div className="crew-cal-label-col">Cuadrilla</div>
          {weekDays.map((d, i) => {
            const isToday = formatDate(d) === formatDate(new Date());
            return <div key={i} className={`crew-cal-day-header ${isToday ? 'today' : ''} ${i >= 5 ? 'weekend' : ''}`}>{getDayLabel(d)}</div>;
          })}
        </div>

        {crews.length === 0 && (
          <div className="empty-state"><Users size={48} /><h3>Sin cuadrillas</h3><p>Crea una cuadrilla para empezar a planificar</p></div>
        )}

        {crews.map(crew => (
          <div key={crew.id} className="crew-cal-row">
            <div className="crew-cal-label" style={{ borderLeftColor: crew.color }}>
              <span className="crew-cal-name">{crew.name}</span>
              <span className="crew-cal-spec">{SPECIALTIES[crew.specialty] || crew.specialty} · {crew.member_count} miembros</span>
              <div className="crew-cal-label-actions">
                <button className="btn-icon-sm" title="Miembros" onClick={() => { setShowMembers(crew); loadMembers(crew.id); }}><UserPlus size={12} /></button>
                <button className="btn-icon-sm" title="Eliminar" onClick={() => deleteCrew(crew.id)}><Trash2 size={12} /></button>
              </div>
            </div>
            {weekDays.map((d, i) => {
              const dayAssigns = getAssignmentsForDay(crew.id, d);
              const isToday = formatDate(d) === formatDate(new Date());
              return (
                <div key={i} className={`crew-cal-cell ${isToday ? 'today' : ''} ${i >= 5 ? 'weekend' : ''}`}
                  onClick={() => { setAssignForm(f => ({ ...f, crewId: crew.id, assignmentDate: formatDate(d), endDate: formatDate(d) })); setShowCreateAssign({}); }}>
                  {dayAssigns.map(a => {
                    const pri = PRIORITIES[a.priority] || PRIORITIES.normal;
                    return (
                      <div key={a.id} className="crew-cal-event" style={{ background: crew.color + '20', borderLeftColor: crew.color }}
                        onClick={e => { e.stopPropagation(); if (window.confirm(`¿Eliminar "${a.title}"?`)) deleteAssignment(a.id); }}>
                        <span className="crew-ev-title">{a.title}</span>
                        <span className="crew-ev-meta">{a.project_code}{a.start_time ? ` · ${a.start_time.slice(0,5)}` : ''}</span>
                        {a.priority !== 'normal' && <span className="crew-ev-pri" style={{ color: pri.color }}>●</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Create Crew Modal */}
      {showCreateCrew && (
        <div className="modal-overlay" onClick={() => setShowCreateCrew(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Nueva Cuadrilla</h3><button className="btn-icon" onClick={() => setShowCreateCrew(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-group"><label>Nombre *</label><input value={crewForm.name} onChange={e => setCrewForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Cuadrilla Eléctrica A" /></div>
              <div className="form-row">
                <div className="form-group"><label>Especialidad</label><select value={crewForm.specialty} onChange={e => setCrewForm(f => ({ ...f, specialty: e.target.value }))}>{Object.entries(SPECIALTIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                <div className="form-group"><label>Color</label><div className="crew-color-picker">{COLORS.map(c => <button key={c} className={`crew-color-btn ${crewForm.color === c ? 'active' : ''}`} style={{ background: c }} onClick={() => setCrewForm(f => ({ ...f, color: c }))} />)}</div></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowCreateCrew(false)}>Cancelar</button><button className="btn btn-primary" onClick={createCrew}>Crear</button></div>
          </div>
        </div>
      )}

      {/* Create Assignment Modal */}
      {showCreateAssign && (
        <div className="modal-overlay" onClick={() => setShowCreateAssign(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Nueva Asignación</h3><button className="btn-icon" onClick={() => setShowCreateAssign(null)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Cuadrilla *</label><select value={assignForm.crewId} onChange={e => setAssignForm(f => ({ ...f, crewId: e.target.value }))}><option value="">Seleccionar...</option>{crews.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                <div className="form-group"><label>Proyecto *</label><select value={assignForm.projectId} onChange={e => setAssignForm(f => ({ ...f, projectId: e.target.value }))}><option value="">Seleccionar...</option>{projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}</select></div>
              </div>
              <div className="form-group"><label>Título/Tarea *</label><input value={assignForm.title} onChange={e => setAssignForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Montaje strings zona A" /></div>
              <div className="form-row">
                <div className="form-group"><label>Fecha inicio *</label><input type="date" value={assignForm.assignmentDate} onChange={e => setAssignForm(f => ({ ...f, assignmentDate: e.target.value }))} /></div>
                <div className="form-group"><label>Fecha fin</label><input type="date" value={assignForm.endDate} onChange={e => setAssignForm(f => ({ ...f, endDate: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Hora inicio</label><input type="time" value={assignForm.startTime} onChange={e => setAssignForm(f => ({ ...f, startTime: e.target.value }))} /></div>
                <div className="form-group"><label>Hora fin</label><input type="time" value={assignForm.endTime} onChange={e => setAssignForm(f => ({ ...f, endTime: e.target.value }))} /></div>
                <div className="form-group"><label>Prioridad</label><select value={assignForm.priority} onChange={e => setAssignForm(f => ({ ...f, priority: e.target.value }))}>{Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
              </div>
              <div className="form-group"><label>Descripción</label><textarea rows={2} value={assignForm.description} onChange={e => setAssignForm(f => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowCreateAssign(null)}>Cancelar</button><button className="btn btn-primary" onClick={createAssignment}>Asignar</button></div>
          </div>
        </div>
      )}

      {/* Members Modal */}
      {showMembers && (
        <div className="modal-overlay" onClick={() => setShowMembers(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Miembros: {showMembers.name}</h3><button className="btn-icon" onClick={() => setShowMembers(null)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="crew-members-list">
                {members.map(m => (
                  <div key={m.id} className="crew-member-row">
                    <span className="crew-member-name">{m.user_name || m.external_name}</span>
                    <span className="crew-member-role">{m.role_in_crew}</span>
                    {m.external_phone && <span className="crew-member-phone">{m.external_phone}</span>}
                    <button className="btn-icon-sm" onClick={() => removeMember(m.id)}><Trash2 size={12} style={{ color: '#EF4444' }} /></button>
                  </div>
                ))}
                {members.length === 0 && <p className="inv-empty">Sin miembros</p>}
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />
              <div className="form-row">
                <div className="form-group"><label>Nombre</label><input value={memberForm.externalName} onChange={e => setMemberForm(f => ({ ...f, externalName: e.target.value }))} placeholder="Nombre del operario" /></div>
                <div className="form-group"><label>Teléfono</label><input value={memberForm.externalPhone} onChange={e => setMemberForm(f => ({ ...f, externalPhone: e.target.value }))} /></div>
                <div className="form-group"><label>Rol</label><select value={memberForm.roleInCrew} onChange={e => setMemberForm(f => ({ ...f, roleInCrew: e.target.value }))}><option value="leader">Jefe</option><option value="member">Operario</option><option value="apprentice">Aprendiz</option></select></div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={addMember}><UserPlus size={14} /> Añadir miembro</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
